import React, { useState, useEffect } from 'react';
import { adminFetch } from '../../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

const LeaderboardTab = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowWeeks, setWindowWeeks] = useState(10);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    adminFetch(`${API_URL}/api/admin/leaderboard?weeks=${windowWeeks}&limit=200`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success) setRows(data.rows);
        else setError(data.message || 'Failed to load');
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [user, windowWeeks]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '18px' }}>Leaderboard — Confirmed Meetings</h2>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: '13px', color: '#6b7280' }}>
          Window:&nbsp;
          <select
            value={windowWeeks}
            onChange={e => setWindowWeeks(Number(e.target.value))}
            style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
          >
            <option value={4}>4 weeks</option>
            <option value={10}>10 weeks</option>
            <option value={26}>26 weeks</option>
            <option value={52}>52 weeks</option>
          </select>
        </label>
      </div>

      <p style={{ color: '#6b7280', fontSize: '12px', marginTop: 0, marginBottom: '12px' }}>
        Confirmed meeting = at least one of the pair marked "Met" in feedback.
      </p>

      {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}
      {error && <p style={{ color: '#dc2626' }}>Error: {error}</p>}

      {!loading && !error && rows.length === 0 && (
        <p style={{ color: '#9ca3af', textAlign: 'center', padding: '32px' }}>No data</p>
      )}

      {!loading && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin2-table">
            <thead>
              <tr>
                <th style={{ width: '48px' }}>#</th>
                <th>Member</th>
                <th>Status</th>
                <th title="All confirmed meetings since the start" style={{ textAlign: 'right' }}>Total met</th>
                <th title={`Confirmed meetings in last ${windowWeeks} weeks`} style={{ textAlign: 'right' }}>{windowWeeks}w met</th>
                <th title="Total matches (confirmed + unconfirmed + not met)" style={{ textAlign: 'right', color: '#9ca3af' }}>Matches</th>
                <th title="Percent of matches that turned into a confirmed meeting" style={{ textAlign: 'right', color: '#9ca3af' }}>Met %</th>
                <th title="Average rating this user RECEIVED from partners (1-4 scale)" style={{ textAlign: 'right' }}>★ Received</th>
                <th title="Average rating this user GAVE to partners (1-4 scale)" style={{ textAlign: 'right' }}>★ Given</th>
                <th>Last match</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const metPct = r.total_matches > 0 ? Math.round((r.total_met / r.total_matches) * 100) : 0;
                return (
                  <tr key={r.member_id}>
                    <td style={{ fontWeight: 600, color: i < 3 ? '#f59e0b' : '#6b7280' }}>{i + 1}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>@{r.tg_username || '—'}</div>
                      <div style={{ color: '#6b7280', fontSize: '12px' }}>{r.name} {r.family || ''}</div>
                    </td>
                    <td><StatusBadge value={r.status} /></td>
                    <td style={{ textAlign: 'right', fontWeight: 600, fontSize: '15px' }}>{r.total_met}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>
                      <span style={{
                        background: r.met_window >= 5 ? '#10b981' : r.met_window >= 2 ? '#f59e0b' : '#e5e7eb',
                        color: r.met_window > 0 ? 'white' : '#6b7280',
                        padding: '2px 10px', borderRadius: '12px', fontSize: '12px',
                      }}>{r.met_window}</span>
                    </td>
                    <td style={{ textAlign: 'right', color: '#9ca3af', fontSize: '12px' }}>{r.total_matches}</td>
                    <td style={{ textAlign: 'right', color: '#9ca3af', fontSize: '12px' }}>{metPct}%</td>
                    <td style={{ textAlign: 'right' }}>
                      <RatingCell value={r.avg_received} count={r.ratings_received_count} />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <RatingCell value={r.avg_given} count={r.ratings_given_count} />
                    </td>
                    <td style={{ fontSize: '12px', color: '#6b7280' }}>{r.last_match_week || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
            {rows.length} active member{rows.length !== 1 ? 's' : ''}
            &nbsp;· Ratings are 1-4 scale from feedback survey.
          </p>
        </div>
      )}
    </div>
  );
};

const RatingCell = ({ value, count }) => {
  if (value == null || count === 0) return <span style={{ color: '#d1d5db' }}>—</span>;
  const num = Number(value);
  // Color: 4.0 deep green, 3.0+ green, 2.0+ amber, below red
  const color = num >= 3.75 ? '#059669'
              : num >= 3.0 ? '#10b981'
              : num >= 2.0 ? '#f59e0b'
              : '#ef4444';
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <span style={{ color, fontWeight: 600, fontSize: '14px' }}>{num.toFixed(2)}</span>
      <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '4px' }}>({count})</span>
    </span>
  );
};

const StatusBadge = ({ value }) => {
  if (!value) return <span style={{ color: '#d1d5db' }}>—</span>;
  const colors = {
    Admin: '#7c3aed', EarlyBird: '#f59e0b', Free: '#6b7280',
    PRO: '#3b82f6', Premium: '#10b981',
  };
  const bg = colors[value] || '#6b7280';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 500, color: 'white', background: bg, whiteSpace: 'nowrap'
    }}>
      {value}
    </span>
  );
};

export default LeaderboardTab;
